import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ModalLoadingOverlay, {
  modalLoadingContainerSx,
} from "./ModalLoadingOverlay";

const DeleteCanidateDrawer = ({
  open,
  underAssignRole,
  handleunderAssignRole,
  usersection,
  basicInfo,
  handleClose,
  handleDelete,
  candidatename,
  acknowledged,
  handleAcknowledgeChange,
  roleList,
}) => {
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await handleDelete();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: modalLoadingContainerSx(submitting, {
          borderRadius: "16px",
          px: 1,
          py: 0.5,
        }),
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          pt: 1,
          px: 3,
        }}
      >
        <Typography
          sx={{ fontSize: "18px", fontWeight: 700, color: "#111827" }}
        >
          {usersection ? "Delete User" : "Delete Candidate"}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: "#6B7280", "&:hover": { backgroundColor: "#F3F4F6" } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        {/* ── Icon circle ── */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5, mt: 1 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "#DBEAFE",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                backgroundColor: "#3B82F6",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {usersection ? (
                <PersonOutlineOutlinedIcon
                  sx={{ color: "#fff", fontSize: 26 }}
                />
              ) : (
                <PersonOffOutlinedIcon sx={{ color: "#fff", fontSize: 26 }} />
              )}
            </Box>
          </Box>
        </Box>

        {/* ── Question ── */}
        <Typography
          sx={{
            textAlign: "center",
            fontSize: "15px",
            fontWeight: 600,
            color: "#111827",
            mb: 2.5,
            lineHeight: 1.5,
          }}
        >
          Are you sure, you want to delete{" "}
          {usersection ? (
            <Typography
              component="span"
              sx={{ color: "#3B82F6", fontWeight: 600, fontSize: "15px" }}
            >
              {basicInfo?.firstName} {basicInfo?.lastName} ({" "}
              {basicInfo?.role_name} ) ?
            </Typography>
          ) : (
            <Typography
              component="span"
              sx={{ color: "#3B82F6", fontWeight: 600, fontSize: "15px" }}
            >
              {candidatename} ?
            </Typography>
          )}
        </Typography>

        {/* ── Warning box ── */}
        <Box
          sx={{
            backgroundColor: "#FEF2F2",
            borderRadius: "10px",
            px: 2.5,
            py: 1.5,
            mb: 2.5,
          }}
        >
          <Typography
            sx={{
              color: "#EF4444",
              fontWeight: 600,
              fontSize: "13.5px",
              mb: 1,
            }}
          >
            {usersection
              ? "If you delete this User"
              : "If you delete this candidate"}
          </Typography>

          {usersection ? (
            <>
              {[
                "The jobs created by the user will be reassigned.",
                "This Candidates created by the user will be reassigned.",
                "Members assigned under the user will be reassigned.",
              ].map((point, idx) => (
                <Typography
                  key={idx}
                  sx={{
                    fontSize: "13px",
                    color: "#374151",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "6px",
                    mb: 0.5,
                    lineHeight: 1.6,
                  }}
                >
                  <span style={{ marginTop: "1px" }}>•</span>
                  <span>{point}</span>
                </Typography>
              ))}
            </>
          ) : (
            <>
              {[
                "The internal profile created for this candidate will be deleted.",
                "This Candidate will be removed from all associated jobs.",
                "All activities related to this candidate will be deleted.",
              ].map((point, idx) => (
                <Typography
                  key={idx}
                  sx={{
                    fontSize: "13px",
                    color: "#374151",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "6px",
                    mb: 0.5,
                    lineHeight: 1.6,
                  }}
                >
                  <span style={{ marginTop: "1px" }}>•</span>
                  <span>{point}</span>
                </Typography>
              ))}
            </>
          )}
        </Box>

        {/* ── Assign Under (user section only) ── */}
        {usersection && (
          <>
            <Typography sx={{ fontSize: "14px", color: "#374151", mb: 1.5 }}>
              <span style={{ color: "#EF4444", fontWeight: 700 }}>*</span>{" "}
              Select a member to transfer authority of all activities associated
              with{" "}
              <strong>
                {basicInfo?.firstName} {basicInfo?.lastName} ({" "}
                {basicInfo?.role_name} )
              </strong>
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="assign-under-label">Assign Under</InputLabel>
              <Select
                labelId="assign-under-label"
                label="Assign Under"
                value={underAssignRole ? underAssignRole.id : ""}
                onChange={handleunderAssignRole}
                sx={{
                  borderRadius: "10px",
                  fontSize: "14px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#D1D5DB",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3F95EA",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3F95EA",
                  },
                }}
              >
                {roleList.map((roleItem) => (
                  <MenuItem
                    key={roleItem.id}
                    value={roleItem.id}
                    sx={{ fontSize: "0.875rem" }}
                  >
                    {roleItem.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        {/* ── Acknowledge checkbox ── */}
        <FormControlLabel
          control={
            <Checkbox
              checked={acknowledged}
              onChange={handleAcknowledgeChange}
              sx={{
                color: "#D1D5DB",
                "&.Mui-checked": { color: "#3B82F6" },
              }}
            />
          }
          label={
            <Typography
              sx={{ fontWeight: 500, fontSize: "14px", color: "#374151" }}
            >
              I acknowledge the implications and wish to proceed.
            </Typography>
          }
        />
      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1,
          gap: 1,
          justifyContent: "flex-end",
        }}
      >
        <Button
          className="cancel-btn"
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            fontSize: "13px",
            letterSpacing: "0.5px",
            borderRadius: "8px",
            borderColor: "#D1D5DB",
            color: "#374151",
            px: 2.5,
            py: 0.8,
            boxShadow: "none",
            "&:hover": {
              borderColor: "#9CA3AF",
              backgroundColor: "#F9FAFB",
              boxShadow: "none",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            (usersection && !(acknowledged && underAssignRole?.id)) ||
            (!usersection && !acknowledged)
          }
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            fontSize: "13px",
            letterSpacing: "0.5px",
            borderRadius: "8px",
            px: 2.5,
            py: 0.8,
            backgroundColor: "#EF4444",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#DC2626", boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: "#FCA5A5", color: "#fff" },
          }}
        >
          Delete
        </Button>
      </DialogActions>
      <ModalLoadingOverlay open={submitting} />
    </Dialog>
  );
};

export default DeleteCanidateDrawer;
